import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Coins } from 'lucide-react';
import { CashOutPanel } from '../../components/check/CashOutPanel';
import { BankLookupPanel } from '../../components/pas/BankLookupPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between py-2 border-b border-slate-700 text-sm">
    <span className="text-slate-400">{label}</span>
    <span className="text-white font-medium text-right break-all ml-4" data-testid={testId}>{value}</span>
  </div>
);

export default function VerifyCheck() {
  const { checkNumber } = useParams();
  const [searchParams] = useSearchParams();
  const claimToken = searchParams.get('t');
  const [data, setData] = useState(null);
  const [justCashed, setJustCashed] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/crypto-check/${checkNumber}/verify`)
      .then(res => setData(res.data))
      .catch(() => setData({ valid: false, message: 'Unable to reach verification service' }));
  }, [checkNumber]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400">Verifying...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-md w-full" data-testid="verify-check-card">
        <div className="flex items-center gap-2 text-emerald-400 mb-6">
          <Coins className="w-6 h-6" /> <span className="font-bold">BETHCOIN Check Verification</span>
        </div>
        <div className="flex items-center gap-3 mb-6">
          {data.valid ? <CheckCircle className="w-10 h-10 text-emerald-400" /> : <XCircle className="w-10 h-10 text-red-400" />}
          <div>
            <p className={`text-xl font-bold ${data.valid ? 'text-emerald-400' : 'text-red-400'}`} data-testid="verify-check-result">
              {data.cashed ? 'Authentic check - already cashed' : data.valid ? 'Valid check' : 'Not valid'}
            </p>
            <p className="text-slate-400 text-sm">{data.message}</p>
          </div>
        </div>
        {data.check_number && (
          <>
            <Row label="Check No." value={data.check_number} testId="verify-check-number" />
            <Row label="Amount" value={`${data.beth_amount} BETH ($${Number(data.usd_value).toFixed(2)})`} testId="verify-check-amount" />
            <Row label="Payee" value={data.payee} testId="verify-check-payee" />
            <Row label="Current holder" value={data.current_holder} testId="verify-check-holder" />
            <Row label="Status" value={data.voided ? 'VOIDED' : data.expired ? 'EXPIRED' : data.status?.toUpperCase()} testId="verify-check-status" />
            <Row label="Signature" value={data.crypto_signature} testId="verify-check-signature" />
            <p className="text-xs text-slate-500 mt-4" data-testid="verify-check-mode-note">
              {data.etherscan_url ? (
                <>E-signature hash recorded on Sepolia/Mainnet. <a href={data.etherscan_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 underline" data-testid="verify-check-etherscan-link">View on Etherscan</a></>
              ) : 'Verified against the BETHCOIN ledger. This check has not been recorded on-chain yet.'}
            </p>
            {data.cashed && (
              <p className="text-sm text-amber-300 mt-4" data-testid="verify-check-cashed-note">
                Cashed {new Date(data.cashed_at).toLocaleString()} to <span className="font-mono break-all">{data.cashed_to}</span>.{' '}
                {data.cash_out_etherscan_url && <a href={data.cash_out_etherscan_url} target="_blank" rel="noopener noreferrer" className="underline">Transfer on Etherscan</a>}
              </p>
            )}
            <div className="mt-4 rounded-xl overflow-hidden border border-slate-700 empty:hidden"><BankLookupPanel params={searchParams} /></div>
            {(justCashed || (data.valid && data.status === 'issued' && !data.cashed)) && (claimToken
              ? <CashOutPanel check={data} claimToken={claimToken} onCashed={() => { setJustCashed(true); load(); }} />
              : <p className="text-xs text-slate-500 mt-4" data-testid="verify-check-scan-note">To cash this check, scan the QR code printed on it.</p>)}
          </>
        )}
        <Link to="/" className="block text-center text-emerald-400 text-sm mt-6" data-testid="verify-check-home-link">BETHCOIN Exchange Market</Link>
      </div>
    </div>
  );
}
